import type { Component, InjectionKey, VNode } from 'vue';
import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockRootContextController } from '#layers/block-engine/utils/block/api/root-context';
import type { UseBlockRenderPropsOptions } from '#layers/block-engine/utils/block/props';
import { defineComponent, getCurrentInstance, h, inject, provide, reactive, useId, useTemplateRef } from 'vue';
import BlockErrorBoundary from '#layers/block-engine/components/Block/BlockErrorBoundary.vue';
import { blockRootContextApiFallbackKey, shouldRegisterBlockRootContext } from '#layers/block-engine/components/Block/BlockRenderShared';
import { useBlockDebuggerStore } from '#layers/block-engine/stores/block-debugger';
import { registerBlockRootContextSource } from '#layers/block-engine/stores/block-root-context';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';
import { createBlockVariables } from '#layers/block-engine/utils/block/variable';
import BlockChildRender from '../../components/Block/BlockChildRender';

export const parentRenderIdKey = Symbol('parentRenderId') as InjectionKey<string | null>;
export const blockChildRenderContextKey = Symbol('blockChildRenderContext') as InjectionKey<BlockChildRenderContext>;
export interface CreateBlockRenderComponentOptions {
	block: BlockSerialized;
	childrenBlocks: Record<string, BlockSerialized | BlockDefinition>;
	/**
	 * This is a function because it will be called after all render blocks are created with `createBlockSerializedRenderComponent`.
	 */
	getBlockRenderComponents: () => Record<string, ReturnType<typeof createBlockSerializedRenderComponent>>;
	skipErrorBoundary?: boolean;
	/**
	 * Context passed to the root block.
	 */
	rootContextApi: BlockRootContextController;
	/**
	 * Trigger events to the root block.
	 */
	emit: (event: string, payload?: any) => void;
	/**
	 * Don't wrap the children blocks in a root div.
	 */
	skipRootDiv?: boolean;
	/**
	 * Don't wrap the block event handlers with the standard payload.
	 */
	unwrapBlockEvents?: boolean;
	/**
	 * Registers this serialized render as a root context source when no parent root context exists.
	 */
	registerRootContext?: boolean;
}

/**
 * Information to be able to render child blocks manually inside other blocks.
 */
export interface BlockChildRenderContext extends UseBlockRenderPropsOptions, Pick<CreateBlockRenderComponentOptions, 'block' | 'childrenBlocks'> {
	blockRenderComponents: Record<string, Component>;
}

export function createBlockSerializedRenderComponent(options: CreateBlockRenderComponentOptions): Component {
	const {
		block,
		childrenBlocks,
		getBlockRenderComponents,
		skipErrorBoundary,
		rootContextApi,
		emit,
		skipRootDiv,
		unwrapBlockEvents,
		registerRootContext,
	} = options;

	return defineComponent({
		name: `Block_${block.name}`,
		props: block.props?.map((p) => p.name) ?? [],
		emits: block.events?.map((e) => e.name) ?? [],
		setup(props, { attrs, slots, expose }) {
			const renderId = useId();
			const inheritedRootContextApi = inject(blockRootContextApiFallbackKey, null);

			const blockRef = useTemplateRef('blockRef');

			const vm = getCurrentInstance();

			// Retrieve block components

			const blockRenderComponents = getBlockRenderComponents();

			// Variables

			const variableIdToName = new Map<string, string>();

			for (const variable of block.variables ?? []) {
				variableIdToName.set(variable.id, variable.name);
			}

			const variables = reactive(createBlockVariables(block));

			// Provided options for the custom child rendering
			const childRenderOptions: BlockChildRenderContext = {
				block,
				blockRenderComponents,
				childrenBlocks,
				variables,
				variableIdToName,
				getRootContext: rootContextApi.getRootContext,
				emit,
				unwrapBlockEvents,
			};

			provide(blockChildRenderContextKey, childRenderOptions);

			// Debug

			const blockDebuggerStore = useBlockDebuggerStore();

			blockDebuggerStore.registerBlock({
				renderId,
				parentRenderId: inject(parentRenderIdKey, null),
				blockId: block.id,
				getVariables() {
					return variables;
				},
				getProps() {
					return props;
				},
			});

			if (registerRootContext === true && shouldRegisterBlockRootContext({
				registerRootContext,
				inheritedRootContextApi,
			})) {
				registerBlockRootContextSource({
					renderId,
					rootContextApi,
				});
			}

			provide(parentRenderIdKey, renderId);

			// Render

			function renderBlockSerialized() {
				// TODO root serialized props

				const children = block.children ?? {};
				const rootChildren = block.rootChildren ?? [];

				function renderSerializedChild(childId: string, root = false, args: any[] = []) {
					const child = children[childId];

					if (isBlockSerializedChild(child)) {
						const childSlots: Record<string, () => VNode[]> = {};

						for (const [slotName, slotChildrenIds] of Object.entries(child.slots)) {
							childSlots[slotName] = (...args: any[]) => slotChildrenIds.flatMap((childId) => renderSerializedChild(childId, false, args)).filter((vnode) => !!vnode);
						}

						return h(BlockChildRender, {
							// Apply attributes manually since it's a root fragment
							...root && skipRootDiv
								? attrs
								: {},
							// Must come after attrs spread to prevent block-level props
							// (e.g. `context` from BlockRootContext) from overwriting
							// BlockChildRender's internal props
							childId: child.id,
							context: childRenderOptions,
						}, childSlots);
					}
					else if (isBlockSerializedSlotOutlet(child)) {
						return slots[child.slotName]?.(...args);
						// TODO fallback
					}

					return null;
				}

				const content = rootChildren.map<any>((childId) => renderSerializedChild(childId, true)).filter(Boolean);

				if (skipRootDiv) {
					return content;
				}

				return h('div', {
					...attrs,
					class: [
						attrs.class,
						'flex flex-col gap-(--block-spacing)',
					],
					ref: 'blockRef',
				}, content);
			}

			// Expose

			expose({
				blockRef,
				variables,
			});

			// Allow debugging in devtools
			if (vm) {
				const privateVm = vm as any;

				privateVm.devtoolsRawSetupState = privateVm.setupState = {
					variables,
				};
			}

			// Render function

			const renderBlock = () => renderBlockSerialized();

			return () => skipErrorBoundary
				? renderBlock()
				: h(BlockErrorBoundary, {
						name: block.name,
						icon: block.icon ?? undefined,
					}, renderBlock);
		},
	});
}
